import React from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { VolumeFormat } from "api/rest/quote-requests";
import { ShipmentPackagingType } from "app/pages/shipments/types/domain";
import { FormProvider, useForm } from "react-hook-form";
import { IncotermsEnum } from "types/Incoterms";
import { RoutingTypeEnum } from "types/RoutingType";

import { useFinalizeAndSendQr } from "../_hooks/useFinalizeAndSendQr";
import { useQuoteRequestSelector } from "../_store";
import { QuoteStepIndex } from "../types";
import CargoDetailsStep from "./_components/CargoDetailsStep";
import PartnersStep from "./_components/PartnersStep";
import ReviewStep from "./_components/ReviewStep";
import RoutingStep from "./_components/RoutingStep";
import ServiceStep from "./_components/ServiceStep";
import ShipmentDetailsStep from "./_components/ShipmentDetailsStep";
import TeamStep from "./_components/TeamStep";
import {
  AirPackageGroupSchema,
  cargoDetailsSchema,
  FinalSchema,
  partnersSchema,
  routingStepSchema,
  serviceSchema,
  shipmentDetailsSchema,
  StopSchema,
  teamSchema,
} from "./_schemas";

const schemaMap = {
  [QuoteStepIndex.Routing]: routingStepSchema,
  [QuoteStepIndex.Service]: serviceSchema,
  [QuoteStepIndex.CargoDetails]: cargoDetailsSchema,
  [QuoteStepIndex.Shipment]: shipmentDetailsSchema,
  [QuoteStepIndex.Partners]: partnersSchema,
  [QuoteStepIndex.Team]: teamSchema,
  [QuoteStepIndex.Review]: teamSchema,
};

export const QuoteRequestAirForm: React.FC = () => {
  const { quoteRequest: quoteRequest, stepIndex } = useQuoteRequestSelector((store) => store.quoteRequestSliceV2);

  const { finalizeAndSendQr, resetLocalState } = useFinalizeAndSendQr();

  const onSubmit = () => {
    finalizeAndSendQr();
  };

  const indexOffset =
    quoteRequest.load_spec?.routing_type === RoutingTypeEnum.D2D ||
    quoteRequest.load_spec?.routing_type === RoutingTypeEnum.D2P
      ? 0
      : 1;

  // @ts-expect-error fine
  const currentSchema = schemaMap[stepIndex];

  const methods = useForm<FinalSchema>({
    mode: "onBlur",
    resolver: zodResolver(currentSchema),
    values: {
      carrierRoutingPref: quoteRequest.carrier_routing_pref_notes,
      serviceLevels: quoteRequest.load_spec?.service_levels ?? [],
      cargo_ready_date: quoteRequest.load_spec?.cargo_ready_date as string,
      incoterms: quoteRequest.load_spec?.incoterms as IncotermsEnum,
      routing_type: quoteRequest.load_spec?.routing_type as RoutingTypeEnum,
      originStop: quoteRequest.stops?.[0 - indexOffset] as StopSchema,
      originPort: quoteRequest.stops?.[1 - indexOffset] as StopSchema,
      destinationPort: quoteRequest.stops?.[2 - indexOffset] as StopSchema,
      destinationStop: quoteRequest.stops?.[3 - indexOffset] as StopSchema,
      airPackageGroups: (quoteRequest.load_spec?.air_package_groups as AirPackageGroupSchema[]) ?? [
        { volume_format: VolumeFormat.Dimensions, packaging_type: ShipmentPackagingType.Cartons, item_quantity: 1 },
      ],
      isHazardous: quoteRequest.is_hazardous ?? false,
      includeCargoInsurance: quoteRequest.insurance_required ?? false,
      includeCustomsClearance: quoteRequest.include_customs_clearance ?? false,
      commodities: quoteRequest.load_spec?.commodities ?? "",
      hazardousDetails: quoteRequest.hazardous_goods_details ?? "",
      poReferenceNumber: quoteRequest.reference_number ?? undefined,
      deadlineRespondAt: quoteRequest.deadline_respond_at ?? undefined,
      additionalNotes: quoteRequest.note ?? undefined,
      partners: quoteRequest.partners ?? [],
      teammates: quoteRequest.teammates ?? [],
    },
  });

  if (resetLocalState) {
    return null;
  }

  return (
    <div>
      <FormProvider {...methods}>
        <RoutingStep methods={methods} onSubmit={onSubmit} />
        <ServiceStep methods={methods} onSubmit={onSubmit} />
        <CargoDetailsStep methods={methods} onSubmit={onSubmit} />
        <ShipmentDetailsStep methods={methods} onSubmit={onSubmit} />
        <PartnersStep methods={methods} onSubmit={onSubmit} />
        <TeamStep methods={methods} onSubmit={onSubmit} />
        <ReviewStep methods={methods} onSubmit={onSubmit} />
      </FormProvider>
    </div>
  );
};
